import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Safely decodes a URI component, returning the original string if decoding fails
 */
const safeDecodeUriComponent = (str: string): string => {
  try {
    return decodeURIComponent(str)
  } catch {
    return str
  }
}

/**
 * Current URL hash without the leading '#', decoded
 * Null if there is no hash
 */
type HashContextValue = string | null

const HashContext = createContext<HashContextValue>(null)

type HashProviderProps = {
  children: ReactNode
}

/**
 * Provides the current URL hash to child components
 * Listens to hashchange events and updates when the hash changes
 *
 * Usage:
 * ```tsx
 * <HashProvider>
 *   <YourComponent />
 * </HashProvider>
 * ```
 */
export const HashProvider = ({ children }: HashProviderProps) => {
  const [hash, setHash] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    const currentHash = window.location.hash.slice(1)
    return currentHash ? safeDecodeUriComponent(currentHash) : null
  })

  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash.slice(1)
      setHash(currentHash ? safeDecodeUriComponent(currentHash) : null)
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return <HashContext.Provider value={hash}>{children}</HashContext.Provider>
}

/**
 * Hook to access the current URL hash
 * Returns null if used outside of HashProvider (graceful fallback)
 *
 * @returns The current hash without '#' (null if no hash or no provider)
 */
export const useHash = (): HashContextValue => {
  return useContext(HashContext)
}

// Helpers for the app's URL-hash deep links, so producers and consumers can't
// drift on the format. `fragment` is the hash without its leading '#'.

// `#comment-<id>` links are owned by AutoScrollToCommentProvider.
const COMMENT_HASH_PREFIX = 'comment-'

export const isCommentHash = (fragment: string) => fragment.startsWith(COMMENT_HASH_PREFIX)

export const getCommentIdFromHashFragment = (fragment: string): string | undefined =>
  isCommentHash(fragment) ? fragment.slice(COMMENT_HASH_PREFIX.length) || undefined : undefined

export const buildCommentHash = (commentId: string) => `#${COMMENT_HASH_PREFIX}${commentId}`
